import { testimonials } from "../data/testimonials";
import { useReveal } from "../hooks/useReveal";

function Card({ t, idx }) {
  return (
    <div className="testimonial-card" data-testid={`testimonial-${idx}`}>
      <div className="testimonial-card__head">
        <div className="testimonial-card__avatar">{t.initials}</div>
        <div>
          <div className="testimonial-card__name">{t.name}</div>
          <div className="testimonial-card__tag">{t.tag}</div>
        </div>
      </div>
      <div className="testimonial-card__stars" aria-label="5 out of 5 stars">
        ★ ★ ★ ★ ★
      </div>
      <p className="testimonial-card__quote">{`"${t.quote}"`}</p>
    </div>
  );
}

export default function Testimonials() {
  const ref = useReveal();
  
  // We take the full array of testimonials and duplicate it once.
  // This creates a long enough line to scroll exactly 50% and then seamlessly loop back to the start.
  const loopAll = [...testimonials, ...testimonials]; 

  return (
    <section
      id="testimonials"
      className="section testimonials"
      ref={ref}
      data-testid="testimonials-section"
      style={{ overflow: 'hidden', paddingBottom: '4rem' }}
    >
      <style>{`
        .marquee-container {
          position: relative;
          width: 100%;
          overflow: hidden;
          margin-top: 3rem;
          display: flex;
        }
        
        .marquee-track {
          display: flex;
          width: max-content;
          gap: 2rem;
          padding-left: 2rem;
          /* Increased the duration to 40s since the track is longer now */
          animation: scrollLeft 40s linear infinite;
        }
        
        /* Pause on hover for readability */
        .marquee-track:hover {
          animation-play-state: paused;
        }

        @keyframes scrollLeft {
          0% { transform: translateX(0); }
          /* -50% shifts it exactly one full set of cards over to create the infinite loop */
          100% { transform: translateX(calc(-50% - 1rem)); } 
        }

        /* Card Styling */
        .testimonial-card {
          background: #ffffff;
          padding: 2rem;
          border-radius: 1rem;
          width: min(380px, 82vw);
          flex-shrink: 0;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          border: 1px solid rgba(0,0,0,0.02);
        }
        .testimonial-card__head {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        .testimonial-card__avatar {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: #f4eae6;
          color: #4D3627;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 1.1rem;
        }
        .testimonial-card__name {
          font-weight: 600;
          color: #333;
        }
        .testimonial-card__tag {
          font-size: 0.85rem;
          color: #888;
        }
        .testimonial-card__stars {
          color: #D3B1A7;
          margin-bottom: 1rem;
          letter-spacing: 2px;
        }
        .testimonial-card__quote {
          font-style: italic;
          color: #555;
          line-height: 1.6;
          margin: 0;
        }
        
        /* Fade edges for a smoother look */
        .marquee-fade-left, .marquee-fade-right {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 10%;
          z-index: 2;
          pointer-events: none;
        }
        
        /* Note: Change 'white' to match your background color (e.g., #F8F1ED) if needed */
        .marquee-fade-left {
          left: 0;
          background: linear-gradient(to right, rgba(255,255,255,1), rgba(255,255,255,0));
        }
        .marquee-fade-right {
          right: 0;
          background: linear-gradient(to left, rgba(255,255,255,1), rgba(255,255,255,0));
        }

        @media (max-width: 1024px) {
          .marquee-track {
            gap: 1.5rem;
            padding-left: 1rem;
            animation-duration: 46s;
          }
          .testimonial-card {
            width: min(340px, 84vw);
            padding: 1.7rem;
          }
        }

        @media (max-width: 640px) {
          .marquee-container {
            margin-top: 2.2rem;
          }
          .marquee-track {
            gap: 1rem;
            padding-left: 0.75rem;
            animation-duration: 52s;
          }
          .testimonial-card {
            width: min(300px, 86vw);
            padding: 1.35rem;
            border-radius: 0.8rem;
          }
          .marquee-fade-left,
          .marquee-fade-right {
            width: 6%;
          }
        }
      `}</style>

      <div className="container reveal reveal--up" style={{ maxWidth: 720 }}>
        <span className="eyebrow">Voices from the Practice</span>
        <h2 className="section-title">Eight years. Two thousand quiet transformations.</h2>
        <span className="divider-leaf">Anonymised with permission</span>
      </div>

      <div className="marquee-container reveal reveal--up">
        {/* Edge fades */}
        <div className="marquee-fade-left"></div>
        <div className="marquee-fade-right"></div>

        {/* Single Row Track */}
        <div className="marquee-track">
          {loopAll.map((t, i) => (
            <Card t={t} idx={`single-${i}`} key={`single-${i}`} />
          ))}
        </div>
      </div>
    </section>
  );
}